import { useState } from 'react'
import {
  Checkbox,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'
import {
  Characteristic,
  DiversityVariantPostData,
  EmpiricalConcept,
  Variable,
} from '../../shared/model/models'
import { diversityVariantAPI } from '../../shared/api/DiversityVariantAPI'
import { translatedDomainName } from '../../shared/utils'

interface IndexedDataItem {
  index: Number
  characteristics: Characteristic[]
  selectedCharacteristic?: Characteristic
  selectedReferenceGroupVariable?: Variable
}

type FsDiversityVariantsProps = {
  onSuccess?: () => void
  onError?: () => void
  characteristics: Characteristic[]
}

const FsDiversityVariants = (props: FsDiversityVariantsProps) => {
  const { onSuccess, onError, characteristics } = props
  const { t } = useTranslation()

  const [checkedDiversityDimensions, setCheckedDiversityDimensions] = useState<IndexedDataItem[]>([
    {
      index: -1,
      characteristics: [],
    },
  ])

  const getDiversityDimensions = () =>
    characteristics
      ?.map((value) => value.empiricalConcept)
      .filter((value) => value.isDiversityDimension)
      .filter((value, idx, self) => self.findIndex((val) => val.id === value.id) === idx)

  const characteristicsForEmpiricalConceptId = (empiricalConceptId: number) =>
    characteristics?.filter(
      (value: Characteristic) => value.empiricalConcept.id === empiricalConceptId,
    )

  const getDiversityCharacteristics = (idx: number): Characteristic[] => {
    const diversityDimensions = getDiversityDimensions()
    let foundCharacteristics: Characteristic[] = []
    if (diversityDimensions && diversityDimensions.at(idx)) {
      const empiricalConcept = diversityDimensions.at(idx)
      foundCharacteristics =
        characteristicsForEmpiricalConceptId(empiricalConcept!.id)?.filter(
          (value) =>
            value.scaleType === 'ORDINAL' ||
            value.scaleType === 'NOMINAL' ||
            value.scaleType === 'BINARY',
        ) || []
    }
    return foundCharacteristics
  }

  const onReferenceVariableChange = (
    e: SelectChangeEvent,
    indexedDataItem: IndexedDataItem | undefined,
  ) => {
    if (indexedDataItem) {
      indexedDataItem.selectedReferenceGroupVariable =
        indexedDataItem.selectedCharacteristic?.variables.find(
          (val) => val.id === Number(e.target.value),
        )
      const newChecked = [...checkedDiversityDimensions]
      setCheckedDiversityDimensions(newChecked)
    }
  }

  const handleToggle = (value: number) => () => {
    const currentIndex = checkedDiversityDimensions.findIndex((val) => val.index === value)
    const newChecked = [...checkedDiversityDimensions]
    const diversityCharacteristics = getDiversityCharacteristics(value)
    if (currentIndex === -1) {
      const defaultSelectedCharacteristic = diversityCharacteristics[0]
      newChecked.push({
        index: value,
        characteristics: diversityCharacteristics,
        selectedCharacteristic: defaultSelectedCharacteristic,
        selectedReferenceGroupVariable: defaultSelectedCharacteristic.variables[0],
      })
    } else {
      newChecked.splice(currentIndex, 1)
    }
    setCheckedDiversityDimensions(newChecked)
  }

  const onDiversityCharacteristicChange = (
    e: SelectChangeEvent,
    indexedDataItem: IndexedDataItem | undefined,
  ) => {
    if (indexedDataItem) {
      indexedDataItem.selectedCharacteristic = indexedDataItem.characteristics?.find(
        (val) => val.id === Number(e.target.value),
      )
      indexedDataItem.selectedReferenceGroupVariable =
        indexedDataItem.selectedCharacteristic?.variables[0]
      const newChecked = [...checkedDiversityDimensions]
      setCheckedDiversityDimensions(newChecked)
    }
  }

  const prepareDiversityVariants = () => {
    const diversityVariants: DiversityVariantPostData[] = []
    checkedDiversityDimensions?.forEach((diversityDimension) => {
      if (
        diversityDimension.index !== -1 &&
        diversityDimension?.selectedCharacteristic &&
        diversityDimension?.selectedReferenceGroupVariable
      ) {
        diversityDimension.selectedCharacteristic.variables
          .filter(
            (variable: Variable) =>
              variable.id !== diversityDimension.selectedReferenceGroupVariable?.id,
          )
          .forEach((variable: Variable) => {
            diversityVariants.push({
              diversityCharacteristicId: diversityDimension.selectedCharacteristic!.id,
              diversityEmpiricalConceptId:
                diversityDimension.selectedCharacteristic!.empiricalConcept.id,
              referenceGroupVariableId: diversityDimension.selectedReferenceGroupVariable!.id,
              groupOfInterestVariableId: variable.id,
            })
          })
      }
    })
    return diversityVariants
  }

  const onSubmit = () => {
    const data = prepareDiversityVariants()
    diversityVariantAPI
      .saveDiversityVariant(data)
      .then(() => onSuccess?.())
      .catch(() => onError?.())
  }

  return (
    <div className="fs-diversity-variants">
      <List sx={{ width: '90%' }}>
        {getDiversityDimensions()?.map((value: EmpiricalConcept, index: number) => {
          const labelId = `checkbox-list-label-${value.id}`
          const checkedIndex = checkedDiversityDimensions.findIndex((val) => val.index === index)
          const indexedDataItem = checkedDiversityDimensions?.at(checkedIndex)
          return (
            <ListItem key={value.id} disablePadding>
              <ListItemButton role={undefined} onClick={handleToggle(index)} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checkedIndex !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ minWidth: '20%' }}
                  id={labelId}
                  primary={t(`empiricalConcept.name.${value.name}` as any)}
                />
              </ListItemButton>
              {checkedIndex !== -1 ? (
                <>
                  <FormControl sx={{ m: 1, minWidth: '30%' }} size="small">
                    <InputLabel
                      id="dd-characteristic-label"
                      sx={{
                        backgroundColor: '#FAF9F6',
                        paddingRight: 1,
                      }}
                    >
                      Diversity Characteristic
                    </InputLabel>
                    <Select
                      value={`${indexedDataItem?.selectedCharacteristic?.id}`}
                      id="diversity-characteristic-select"
                      label="Characteristic"
                      onChange={(e) => onDiversityCharacteristicChange(e, indexedDataItem)}
                    >
                      {checkedDiversityDimensions
                        ?.at(checkedIndex)
                        ?.characteristics.map((characteristic: Characteristic) => (
                          <MenuItem
                            value={characteristic.id}
                            key={`characteristic-item-${characteristic.id}`}
                          >
                            {characteristic.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Box sx={{ width: 5 }} />
                  <FormControl sx={{ m: 1, minWidth: '30%' }} size="small">
                    <InputLabel
                      id="dd-reference-label"
                      sx={{
                        backgroundColor: '#FAF9F6',
                        paddingRight: 1,
                      }}
                    >
                      Reference Group Variable
                    </InputLabel>
                    <Select
                      value={`${indexedDataItem?.selectedReferenceGroupVariable?.id}`}
                      id="reference-variable-select"
                      label="Variable"
                      onChange={(e) => onReferenceVariableChange(e, indexedDataItem)}
                    >
                      {checkedDiversityDimensions
                        ?.at(checkedIndex)
                        ?.selectedCharacteristic?.variables.map((variable: Variable) => (
                          <MenuItem value={variable.id} key={`variable-item-${variable.id}`}>
                            {translatedDomainName(variable.label, t)}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </>
              ) : null}
            </ListItem>
          )
        })}
      </List>
      <Button
        disabled={checkedDiversityDimensions.length <= 1}
        sx={{ mt: 1, mr: 1 }}
        onClick={onSubmit}
      >
        Submit
      </Button>
    </div>
  )
}

export default FsDiversityVariants
